import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../../shared/Icon';
import { SURFACE_CENTRAL, TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../../../utils/statusColors';
import { procurementService } from '../../../services/api';
import { fmtDate } from '../../../utils/formatters';
import { campo, mensagemDoErroDeCarga } from '../../../design';
import { useConfirm } from '../../shared/ConfirmDialog';
import { montarArvore, idsDe, estadoDaMarcacao, alternarPasta, validadeDoDocumento, Pasta, Subpasta } from '../../../utils/arvoreDeDocumentos';
import { ordemDoDossie, mover } from '../../../utils/ordemDoDossie';

/**
 * Publicar os documentos para o órgão: escolhe o que entra, gera os links
 * públicos e devolve um endereço para mandar por e-mail.
 *
 * Substitui o caminho de 14/08/2026, em que a página foi montada por um script
 * fora do sistema e subiu por scp: o que foi publicado, para qual licitação e
 * com que validade agora fica registrado, e republicar é um clique.
 *
 * O SELETOR (14/10/2026, PR-1, plano aprovado pelo Heitor):
 *  - a mesma árvore de Licitações → Documentos (pasta → grupo → subpasta), com checkbox por pasta: marcar a pasta marca
 *    os filhos; parcial fica indeterminado (utils/arvoreDeDocumentos);
 *  - vencido e vencendo APARECEM, com selo. Antes o vencido sumia da lista, e quem procurava o documento achava que não
 *    existia. Marcado vencido = aviso fixo e pergunta antes de publicar (sem trava dura);
 *  - os que já têm link público: o build sobrescreve o prazo deles com o desta publicação (backend, até a correção do
 *    Bastião), e a tela diz quantos;
 *  - a carga é estado: falhou = diz e oferece Tentar de novo, nunca a lista vazia como se não houvesse documento.
 * O token do link quem gera é o build (procurement-publication.service): documento sem link público ganha um ao publicar.
 */

interface Props {
  itemId: number;
  /** Documentos gerados da proposta (declarações, proposta financeira). */
  generatedDocs: any[];
  showToast: (msg: string, tipo?: 'success' | 'error') => void;
}

const SELO: Record<'vencido' | 'vencendo', { rotulo: string; cls: string }> = {
  vencido: { rotulo: 'vencido', cls: TONE_BADGE.danger },
  vencendo: { rotulo: 'vence em breve', cls: TONE_BADGE.warning },
};

/** Checkbox com o estado "alguns" (indeterminado), que o atributo do HTML não tem. */
function CaixaDaPasta({ estado, onChange, rotulo }: { estado: 'todos' | 'alguns' | 'nenhum'; onChange: () => void; rotulo: string }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = estado === 'alguns'; }, [estado]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={rotulo}
      checked={estado === 'todos'}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      className="accent-primary"
    />
  );
}

export default function PublicacaoPanel({ itemId, generatedDocs, showToast }: Props) {
  const confirmar = useConfirm();
  const [aberto, setAberto] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erroDaCarga, setErroDaCarga] = useState<string | null>(null);
  const [publicando, setPublicando] = useState(false);
  const [publicacao, setPublicacao] = useState<any | null>(null);
  const [documentos, setDocumentos] = useState<any[]>([]);
  const [selDocs, setSelDocs] = useState<Set<number>>(new Set());
  const [selGerados, setSelGerados] = useState<Set<number>>(new Set());
  const [linkDays, setLinkDays] = useState('180');
  const [comDossie, setComDossie] = useState(true);
  // A ordem que a pessoa escolheu para o arquivo único (a gravada vem do config). A mostrada e a gravada é sempre a
  // lista INTEIRA dos marcados: ver utils/ordemDoDossie.
  const [ordemEscolhida, setOrdemEscolhida] = useState<number[]>([]);
  const [arrastando, setArrastando] = useState<number | null>(null);
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    if (!aberto) return;
    let vivo = true;
    (async () => {
      setCarregando(true);
      setErroDaCarga(null);
      try {
        const [pubs, docs] = await Promise.all([
          procurementService.getPublications(itemId),
          procurementService.getDocuments(),
        ]);
        if (!vivo) return;
        const lista = Array.isArray(docs) ? docs : docs?.data || [];
        // O que pode ir para o órgão: com arquivo, ativo e não arquivado. O VENCIDO fica (com selo): sumir com ele fazia
        // quem procurava o documento achar que ele não existia.
        setDocumentos(lista.filter((d: any) => d.s3Key && !d.archived && d.isActive !== false));
        const atual = Array.isArray(pubs) && pubs.length ? pubs[0] : null;
        setPublicacao(atual);
        if (atual?.config) {
          setSelDocs(new Set(atual.config.documentIds || []));
          setSelGerados(new Set(atual.config.generatedDocumentIds || []));
          setOrdemEscolhida(Array.isArray(atual.config.dossierDocumentIds) ? atual.config.dossierDocumentIds : []);
          if (atual.config.linkDays) setLinkDays(String(atual.config.linkDays));
        }
      } catch (e: any) {
        if (vivo) setErroDaCarga(mensagemDoErroDeCarga(e));
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => { vivo = false; };
  }, [aberto, itemId, tentativa]);

  const arvore = useMemo(() => montarArvore(documentos), [documentos]);
  const gerados = generatedDocs.filter((d: any) => d.status !== 'cancelado');

  const alternar = (set: Set<number>, setter: (s: Set<number>) => void, id: number) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  };

  const marcados = documentos.filter((d) => selDocs.has(d.id));
  const porId = useMemo(() => new Map(documentos.map((d) => [d.id, d])), [documentos]);
  const ordem = useMemo(
    () => ordemDoDossie(ordemEscolhida, arvore.flatMap((p) => idsDe(p)).filter((id) => selDocs.has(id))),
    [ordemEscolhida, arvore, selDocs],
  );
  const moverNoDossie = (de: number, para: number) => setOrdemEscolhida(mover(ordem, de, para));
  const vencidosMarcados = marcados.filter((d) => validadeDoDocumento(d) === 'vencido');
  const comLinkMarcados = marcados.filter((d) => d.publicToken);
  const dias = parseInt(linkDays, 10) || 180;

  const publicar = useCallback(async () => {
    if (selDocs.size === 0 && selGerados.size === 0) {
      showToast('Selecione ao menos um documento', 'error');
      return;
    }
    if (vencidosMarcados.length > 0) {
      const ok = await confirmar({
        title: `Publicar com ${vencidosMarcados.length} documento(s) vencido(s)?`,
        message: vencidosMarcados.map((d) => `${d.name} (venceu em ${fmtDate(d.expiryDate)})`).join(' · '),
        consequence: 'O órgão vai ver a data de validade no link. Se for possível, renove antes e publique de novo.',
        confirmLabel: 'Publicar mesmo assim',
      });
      if (!ok) return;
    }
    setPublicando(true);
    try {
      const config = {
        documentIds: Array.from(selDocs),
        generatedDocumentIds: Array.from(selGerados),
        // Sempre a lista inteira dos marcados, na ordem da tela: com a lista preenchida, o backend monta o dossiê SÓ
        // com ela.
        dossierDocumentIds: ordem,
        linkDays: dias,
      };
      const pub = publicacao
        ? await procurementService.updatePublication(publicacao.id, { config })
        : await procurementService.createPublication(itemId, { config });
      const resultado = await procurementService.buildPublication(pub.id, {
        linkDays: config.linkDays,
        buildDossier: comDossie,
      });
      setPublicacao(resultado);
      showToast('Página publicada');
    } catch (e: any) {
      showToast('Erro ao publicar: ' + (e?.response?.data?.message || e?.message), 'error');
    } finally {
      setPublicando(false);
    }
  }, [selDocs, selGerados, ordem, vencidosMarcados, confirmar, dias, publicacao, itemId, comDossie, showToast]);

  const url = publicacao?.url
    || (publicacao?.slug ? `https://financeiro.ipcom.com.br/api/p/licitacao/${publicacao.slug}` : '');

  const linhaDoDoc = (d: any) => {
    const v = validadeDoDocumento(d);
    return (
      <label key={d.id} className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-central-elevado cursor-pointer" data-doc={d.id}>
        <input type="checkbox" checked={selDocs.has(d.id)} onChange={() => alternar(selDocs, setSelDocs, d.id)} className="accent-primary" />
        <span className="text-xs text-slate-200 flex-1 truncate">{d.name}</span>
        {v !== 'ok' && <span className={`text-[10px] px-1.5 py-0.5 rounded ${SELO[v].cls}`} data-validade={v}>{SELO[v].rotulo}</span>}
        {d.expiryDate && !d.noExpiry && <span className="text-[10px] text-slate-500 tabular-nums">{fmtDate(d.expiryDate)}</span>}
      </label>
    );
  };

  const subpasta = (s: Subpasta, nivel: number) => {
    const ids = idsDe(s);
    return (
      <details key={s.chave} className={nivel ? 'ml-3' : ''} open={ids.some((id) => selDocs.has(id))}>
        <summary className="flex items-center gap-2 px-1.5 py-1 cursor-pointer text-xs text-slate-300">
          <CaixaDaPasta estado={estadoDaMarcacao(ids, selDocs)} onChange={() => setSelDocs(alternarPasta(ids, selDocs))} rotulo={`Marcar a subpasta ${s.nome}`} />
          <span className="flex-1 truncate">{s.nome}</span>
          <span className="text-[10px] text-slate-500 tabular-nums">{ids.length}</span>
        </summary>
        <div className="ml-4">{s.docs.map(linhaDoDoc)}</div>
      </details>
    );
  };

  const pasta = (p: Pasta) => {
    const ids = idsDe(p);
    return (
      <details key={p.chave} open={ids.some((id) => selDocs.has(id))} data-pasta={p.chave}>
        <summary className="flex items-center gap-2 px-1.5 py-1.5 cursor-pointer text-[11px] uppercase tracking-wide text-slate-400">
          <CaixaDaPasta estado={estadoDaMarcacao(ids, selDocs)} onChange={() => setSelDocs(alternarPasta(ids, selDocs))} rotulo={`Marcar a pasta ${p.nome}`} />
          <span className="flex-1">{p.nome}</span>
          <span className="text-[10px] text-slate-500 tabular-nums normal-case">{ids.filter((id) => selDocs.has(id)).length}/{ids.length}</span>
        </summary>
        <div className="ml-3">
          {p.soltos.map(linhaDoDoc)}
          {p.itens.map((i) => (i.tipo === 'grupo' ? (
            <details key={i.chave} open={idsDe(i).some((id) => selDocs.has(id))}>
              <summary className="flex items-center gap-2 px-1.5 py-1 cursor-pointer text-xs text-slate-300">
                <CaixaDaPasta estado={estadoDaMarcacao(idsDe(i), selDocs)} onChange={() => setSelDocs(alternarPasta(idsDe(i), selDocs))} rotulo={`Marcar o grupo ${i.nome}`} />
                <span className="flex-1 truncate">{i.nome}</span>
                <span className="text-[10px] text-slate-500 tabular-nums">{idsDe(i).length}</span>
              </summary>
              {i.subpastas.map((s) => subpasta(s, 1))}
            </details>
          ) : subpasta(i, 0)))}
        </div>
      </details>
    );
  };

  return (
    <div className={`${SURFACE_CENTRAL.card} p-4 space-y-3`}>
      <button
        onClick={() => setAberto(!aberto)}
        className="w-full flex items-center justify-between text-sm text-slate-200 font-medium"
      >
        <span className="flex items-center gap-2">
          <Icon name="link" className="w-4 h-4 text-slate-400" />
          Publicar documentos para o órgão
        </span>
        <span className="text-xs text-slate-500">
          {publicacao?.publishedAt ? `publicado em ${fmtDate(publicacao.publishedAt)}` : 'não publicado'}
        </span>
      </button>

      {aberto && (
        <div className="space-y-3">
          {carregando && <div className="text-xs text-slate-500">Carregando documentos…</div>}

          {!carregando && erroDaCarga && (
            <div role="alert" className={`px-3 py-2 rounded-lg border text-xs flex items-center justify-between gap-3 ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`} data-publicacao-sem-leitura>
              <span>Não consegui ler os documentos ({erroDaCarga}). Sem a lista, não dá para escolher o que vai para o órgão.</span>
              <button type="button" onClick={() => setTentativa((t) => t + 1)} className="shrink-0 underline font-medium">Tentar de novo</button>
            </div>
          )}

          {url && publicacao?.publishedAt && (
            <div className="bg-central-elevado rounded-lg border border-central-borda px-3 py-2 flex items-center gap-2">
              <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-200 hover:underline truncate flex-1">
                {url}
              </a>
              <button
                onClick={() => { navigator.clipboard?.writeText(url); showToast('Endereço copiado'); }}
                className="px-2 py-1 text-[10px] bg-central-cartao border border-central-borda hover:bg-central-elevado text-slate-200 rounded"
              >
                Copiar
              </button>
            </div>
          )}

          {!carregando && !erroDaCarga && (
            <>
              <div className="max-h-80 overflow-y-auto space-y-1 pr-1 border border-central-borda rounded-lg p-2 bg-central-elevado" data-arvore-de-documentos>
                {arvore.length === 0 && <p className="text-xs text-slate-500 px-1.5 py-1">Nenhum documento de habilitação com arquivo.</p>}
                {arvore.map(pasta)}
                {gerados.length > 0 && (
                  <div className="pt-2 mt-2 border-t border-central-borda">
                    <div className="text-[11px] uppercase tracking-wide text-slate-400 px-1.5 mb-1">Proposta e declarações desta licitação</div>
                    {gerados.map((d: any) => (
                      <label key={d.id} className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-central-elevado cursor-pointer">
                        <input type="checkbox" checked={selGerados.has(d.id)} onChange={() => alternar(selGerados, setSelGerados, d.id)} className="accent-primary" />
                        <span className="text-xs text-slate-200 flex-1 truncate">{d.name}</span>
                        <span className="text-[10px] text-slate-500">{d.status}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {vencidosMarcados.length > 0 && (
                <p role="alert" className={`text-xs ${TONE_TEXT.warning}`} data-aviso-vencidos>
                  <Icon name="alerta" className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                  {vencidosMarcados.length === 1 ? '1 documento vencido vai' : `${vencidosMarcados.length} documentos vencidos vão`} no link:{' '}
                  {vencidosMarcados.map((d) => d.name).join(', ')}. O órgão vai ver a data.
                </p>
              )}
              {comLinkMarcados.length > 0 && (
                <p className="text-xs text-slate-400" data-aviso-prazo-do-link>
                  {comLinkMarcados.length === 1 ? '1 documento marcado já tem' : `${comLinkMarcados.length} documentos marcados já têm`} link público.
                  Publicar muda o prazo do link deles para {dias} dias, inclusive o que já foi mandado a outro órgão.
                </p>
              )}

              <div className="flex items-end gap-3 flex-wrap">
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-slate-500 block mb-1">Validade do link (dias)</label>
                  <input
                    value={linkDays}
                    onChange={(e) => setLinkDays(e.target.value)}
                    className={`w-28 rounded-lg px-3 py-1.5 text-sm outline-none ${campo}`}
                    placeholder="180"
                  />
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-300 pb-2 cursor-pointer">
                  <input type="checkbox" checked={comDossie} onChange={(e) => setComDossie(e.target.checked)} className="accent-primary" />
                  Gerar também o arquivo único (capa com índice + documentos)
                </label>
                <button
                  onClick={publicar}
                  disabled={publicando}
                  className="ml-auto px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-strong disabled:opacity-50"
                >
                  {publicando ? 'Publicando…' : publicacao?.publishedAt ? 'Republicar' : 'Publicar'}
                </button>
              </div>
              {comDossie && ordem.length > 0 && (
                <div className="rounded-lg border border-central-borda bg-central-elevado p-2" data-ordem-do-dossie>
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 px-1.5 mb-1">Ordem no arquivo único</div>
                  <ol className="space-y-0.5">
                    {ordem.map((id, i) => {
                      const d = porId.get(id);
                      return (
                        <li
                          key={id}
                          draggable
                          onDragStart={() => setArrastando(i)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => { e.preventDefault(); if (arrastando !== null) moverNoDossie(arrastando, i); setArrastando(null); }}
                          onDragEnd={() => setArrastando(null)}
                          className={`flex items-center gap-2 px-1.5 py-1 rounded text-xs text-slate-200 cursor-grab ${arrastando === i ? 'opacity-50' : 'hover:bg-central-elevado'}`}
                          data-dossie-doc={id}
                        >
                          <span className="w-5 text-right text-slate-500 tabular-nums">{i + 1}.</span>
                          <span className="flex-1 truncate">{d?.name ?? `Documento #${id}`}</span>
                          <button type="button" onClick={() => moverNoDossie(i, i - 1)} disabled={i === 0} aria-label={`Subir ${d?.name ?? id}`} className="px-1 text-slate-400 hover:text-white disabled:opacity-30">↑</button>
                          <button type="button" onClick={() => moverNoDossie(i, i + 1)} disabled={i === ordem.length - 1} aria-label={`Descer ${d?.name ?? id}`} className="px-1 text-slate-400 hover:text-white disabled:opacity-30">↓</button>
                        </li>
                      );
                    })}
                  </ol>
                  <p className="text-[11px] text-slate-500 px-1.5 mt-1">
                    Arraste ou use as setas. A capa com o índice segue esta ordem.
                    {selGerados.size > 0 && ' A proposta e as declarações vão na página, mas não entram no arquivo único.'}
                  </p>
                </div>
              )}
              <p className="text-[11px] text-slate-500">
                {selDocs.size + selGerados.size} documento(s) selecionado(s). A página mostra só o que está aqui, e cada
                arquivo é servido por um link próprio que expira no prazo acima. Documento sem link público ganha um ao publicar.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
